/**
 * StakeholderMapSkeleton
 *
 * A skeleton loading state for the StakeholderMap component
 */

import React from 'react'
import { cn } from '@/lib/utils'

interface StakeholderMapSkeletonProps {
  className?: string
}

export const StakeholderMapSkeleton: React.FC<StakeholderMapSkeletonProps> = ({ className }) => {
  return (
    <div
      className={cn(
        'border-border bg-card my-4 flex animate-pulse flex-col overflow-hidden rounded-lg border shadow-sm transition-all',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center border-b p-3">
        <div className="flex items-center gap-2">
          <div className="size-4 rounded bg-gray-200 dark:bg-gray-700"></div>
          <div className="h-4 w-32 rounded bg-gray-200 dark:bg-gray-700"></div>
        </div>
      </div>

      {/* Stakeholder map visualization */}
      <div className="p-4">
        <div className="flex h-[300px] w-full items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800">
          <div className="relative size-full">
            {/* Quadrant lines */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-full w-px bg-gray-300 dark:bg-gray-600"></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-px w-full bg-gray-300 dark:bg-gray-600"></div>
            </div>

            {/* Quadrant labels */}
            <div className="absolute left-2 top-2">
              <div className="h-3 w-16 rounded bg-gray-200 dark:bg-gray-700"></div>
            </div>
            <div className="absolute right-2 top-2">
              <div className="h-3 w-16 rounded bg-gray-200 dark:bg-gray-700"></div>
            </div>
            <div className="absolute bottom-2 left-2">
              <div className="h-3 w-16 rounded bg-gray-200 dark:bg-gray-700"></div>
            </div>
            <div className="absolute bottom-2 right-2">
              <div className="h-3 w-16 rounded bg-gray-200 dark:bg-gray-700"></div>
            </div>

            {/* Stakeholder dots */}
            <div className="absolute left-1/4 top-1/4 -translate-x-1/2 -translate-y-1/2">
              <div className="size-8 rounded-full bg-blue-200 dark:bg-blue-900"></div>
            </div>
            <div className="absolute right-1/4 top-1/4 -translate-y-1/2 translate-x-1/2">
              <div className="size-6 rounded-full bg-green-200 dark:bg-green-900"></div>
            </div>
            <div className="absolute bottom-1/3 left-1/3 -translate-x-1/2 translate-y-1/2">
              <div className="size-10 rounded-full bg-red-200 dark:bg-red-900"></div>
            </div>
            <div className="absolute bottom-1/4 right-1/3 translate-x-1/2 translate-y-1/2">
              <div className="size-7 rounded-full bg-yellow-200 dark:bg-yellow-900"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Stakeholder list */}
      <div className="border-t p-4">
        <div className="space-y-3">
          {/* Stakeholder items */}
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <div
                className={`size-6 rounded-full ${
                  i === 0
                    ? 'bg-blue-200 dark:bg-blue-900'
                    : i === 1
                      ? 'bg-green-200 dark:bg-green-900'
                      : i === 2
                        ? 'bg-red-200 dark:bg-red-900'
                        : 'bg-yellow-200 dark:bg-yellow-900'
                }`}
              ></div>
              <div className="flex-1 space-y-1">
                <div className="h-4 w-1/3 rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="h-3 w-2/3 rounded bg-gray-200 dark:bg-gray-700"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
